import type { CaseAcceptanceOverviewKpiData } from '@services/api/dashboard/case-acceptance.api'
import type { DiagnosedTreatmentKpiData } from '@services/api/dashboard/diagnosed-treatment.api'
import type { DashboardMetricItemData } from '../types/dashboardMetrics.types'
import { getOverviewUtilizationRow2Metrics } from '@components/pages/Dashboard/Overview/overviewMetrics.mockData'
import type { RangeKey } from '@dataTypes/dashboard.types'
import { getCaseAcceptanceConfig } from '@components/pages/Dashboard/Overview/dashboardOverview.mockData'
import {
  isDashboardDevMocksEnabled,
  withDashboardMetricShellValues,
} from '@utils/dashboardDevMocks.util'

const EMPTY_CASE_ACCEPTANCE_DONUT: ReturnType<typeof getCaseAcceptanceConfig> =
  {
    primaryValue: 0,
    secondaryValue: 0,
    valueSeparator: ' vs ',
    slices: [
      { name: 'Acceptance rate', value: 0, color: 'var(--blue-400)' },
      { name: 'Not accepted', value: 100, color: 'var(--orange-300)' },
    ],
    valueFormatter: (val: number): string => `${val}%`,
    acceptedVal: '—',
    sameDayVal: '—',
    pendingVal: '—',
  }

export function mapDiagnosedTreatmentToMetric(
  kpis: DiagnosedTreatmentKpiData,
): DashboardMetricItemData {
  return {
    id: 'diagnosed-tx',
    leftLabel: 'Diagnosed treatment',
    rightLabel: kpis.diagnosedTreatmentGoal,
    value: kpis.diagnosedTreatmentCount,
  }
}

export function mapCaseAcceptanceRateToMetric(
  kpis: CaseAcceptanceOverviewKpiData,
): DashboardMetricItemData {
  return {
    id: 'case-acceptance',
    leftLabel: 'Case acceptance',
    rightLabel: kpis.acceptanceGoal,
    value: kpis.caseAcceptanceRate,
  }
}

export function mapCaseAcceptanceOverviewToMetrics(
  kpis: CaseAcceptanceOverviewKpiData,
): DashboardMetricItemData[] {
  return [
    {
      id: 'case-accepted-value',
      leftLabel: 'Accepted value',
      value: kpis.acceptedVal,
    },
    {
      id: 'case-same-day-value',
      leftLabel: 'Same-day value',
      value: kpis.sameDayVal,
    },
    {
      id: 'case-pending-value',
      leftLabel: 'Pending value',
      value: kpis.pendingVal,
    },
  ]
}

export function buildUtilizationRow2Metrics(
  range: RangeKey,
  diagnosedKpis: DiagnosedTreatmentKpiData | null | undefined,
  caseKpis: CaseAcceptanceOverviewKpiData | null | undefined,
): DashboardMetricItemData[] {
  const shells = withDashboardMetricShellValues(
    getOverviewUtilizationRow2Metrics(range),
    isDashboardDevMocksEnabled(),
  )
  if (!diagnosedKpis && !caseKpis) {
    return shells
  }
  return shells.map((metric) => {
    if (metric.id === 'diagnosed-tx' && diagnosedKpis) {
      return mapDiagnosedTreatmentToMetric(diagnosedKpis)
    }
    if (metric.id === 'case-acceptance' && caseKpis) {
      return mapCaseAcceptanceRateToMetric(caseKpis)
    }
    return metric
  })
}

export function buildCaseAcceptanceDonutFromOverview(
  kpis: CaseAcceptanceOverviewKpiData | null | undefined,
  range: RangeKey,
): ReturnType<typeof getCaseAcceptanceConfig> {
  const mock = getCaseAcceptanceConfig(range)
  if (!kpis) {
    return isDashboardDevMocksEnabled() ? mock : EMPTY_CASE_ACCEPTANCE_DONUT
  }
  const rateDisplay = kpis.caseAcceptanceRate
  const isNaRate = rateDisplay === '—'
  const primaryValue = isNaRate ? 0 : Math.min(100, kpis.primaryValue)
  const notAccepted = Math.max(0, 100 - primaryValue)
  return {
    ...mock,
    primaryValue,
    secondaryValue: kpis.secondaryValue,
    acceptedVal: kpis.acceptedVal,
    sameDayVal: kpis.sameDayVal,
    pendingVal: kpis.pendingVal,
    valueFormatter: (val: number) => {
      if (isNaRate && val === primaryValue) return rateDisplay
      return mock.valueFormatter(val)
    },
    slices: [
      {
        name: 'Acceptance rate',
        value: primaryValue,
        color: 'var(--blue-400)',
      },
      {
        name: 'Not accepted',
        value: notAccepted,
        color: 'var(--orange-300)',
      },
    ],
  }
}
